import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { formatPrice } from '@/data/money';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import ErrorState from '@/components/ErrorState';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Payment = () => {
  const { requestId, astrologerId } = useParams();
  const { user, config, rates, displayCurrency } = useAuth();
  const free = config?.free_mode;
  const navigate = useNavigate();
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [astrologerId, requestId]);

  const fetchData = async () => {
    try {
      // Use the (blind) answer payload — it carries the alias + the profile fields a
      // client is allowed to see pre-payment. No real identity is fetched here.
      const res = await axios.get(`${API}/requests/${requestId}/answers`, { withCredentials: true });
      const found = res.data.find((a) => a.astrologer_id === astrologerId);
      setAnswer(found || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('無法載入資料');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!free || processing) return;
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/orders`,
        { request_id: requestId, astrologer_id: astrologerId },
        { withCredentials: true }
      );
      toast.success('已選擇命理師！');
      navigate(`/chat/${response.data.order_id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || '付款失敗');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">載入中...</p>
      </div>
    );
  }

  if (!answer) {
    return <ErrorState message="找不到命理師資料" />;
  }
  if (!free) {
    return <ErrorState message="付款功能尚未啟用，暫時無法開通諮詢室，請稍後再試。" />;
  }

  const profile = answer.astrologer_profile || {};

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4">
        <Link to="/client/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to={`/client/compare/${requestId}`}>
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-8 text-center">
            {free ? '確認選擇' : '確認付款'}
          </h1>

          <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 mb-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-[#C93B2B] text-white rounded-sm flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-serif text-[#1A1A1A] mb-2">
                {answer.astrologer_name || '你選擇的命理師'}
              </h2>
              <p className="text-sm text-[#5C5C5C]">{free ? '確認後即可得知命理師的顯示名稱並開始私訊' : '付款後即可在諮詢室得知對方的顯示名稱並開始私訊'}</p>
            </div>

            <div className="border-t border-[#E5E0D8] pt-6 mb-6">
              {profile.specialties && profile.specialties.length > 0 && (
                <div className="flex justify-between mb-3">
                  <span className="text-sm text-[#5C5C5C]">專長</span>
                  <span className="text-sm text-[#1A1A1A] font-medium">{profile.specialties.join(', ')}</span>
                </div>
              )}
              <div className="mb-3">
                <span className="text-sm text-[#5C5C5C]">命理師收費（逐範圍）</span>
                <div className="mt-1 space-y-1">
                  {(answer.quotes && answer.quotes.length
                    ? answer.quotes
                    : [{ scope: '諮詢', price: answer.price, consultation_time_minutes: answer.consultation_time_minutes }]
                  ).map((q) => {
                    const f = formatPrice(q.price, answer.currency, displayCurrency, rates);
                    return (
                      <div key={q.scope} className="flex justify-between text-sm">
                        <span className="text-[#5C5C5C]">{q.scope} · {q.consultation_time_minutes} 分鐘</span>
                        <span className="text-right text-[#1A1A1A] font-medium">{f.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {profile.region && (
                <div className="flex justify-between mb-3">
                  <span className="text-sm text-[#5C5C5C]">地區</span>
                  <span className="text-sm text-[#1A1A1A] font-medium">{profile.region}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-[#5C5C5C]">服務方式</span>
                <span className="text-sm text-[#1A1A1A] font-medium">
                  {profile.online_service ? '線上諮詢' : '面談'}
                </span>
              </div>
            </div>

            <div className="border-t border-[#E5E0D8] pt-6">
              {free ? (
                <div className="bg-[#F5F0E6] rounded-sm p-4 mb-6">
                  <p className="text-base font-serif text-[#1A1A1A]">平台免費開通諮詢室</p>
                  <p className="text-xs text-[#5C5C5C] mt-2">平台不收取任何費用。上方為命理師的收費，你可於諮詢室與命理師商議付款方式。</p>
                </div>
              ) : (
                <div className="bg-[#F5F0E6] rounded-sm p-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-serif text-[#1A1A1A]">總計</span>
                    {(() => {
                      const f = formatPrice(answer.price, answer.currency, displayCurrency, rates);
                      return (
                        <span className="text-2xl font-serif text-[#C93B2B]">{f.text}</span>
                      );
                    })()}
                  </div>
                </div>
              )}

              <Button
                onClick={handlePayment}
                disabled={processing || !free}
                className="w-full bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm py-6 text-lg shadow-sm"
                data-testid="confirm-payment-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" strokeWidth={1.5} />
                    處理中...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2" strokeWidth={1.5} />
                    {free ? '選擇並開通諮詢室' : '確認付款'}
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="bg-[#FEF8F0] border border-[#C93B2B] rounded-sm p-6 text-center">
            <p className="text-sm text-[#1A1A1A]">
              🔒 {free ? '確認後' : '付款後'}，你將能與這位命理師在平台內進行私訊交流。所有溝通請在平台內進行，命理師只會見到你的暱稱。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
